// HEAL-46a D-25 — исходы отправки, которых сервер не знает.
//
// Человек выбрал пять файлов, загрузилось четыре — и никакого объяснения. Строка
// для отклонённого не создаётся вовсе (дубль отбивается раньше записи), поэтому в
// составе пакета его нет. Человек думает, что файл потерялся.
//
// Корень, названный PM: «я не знаю, какие файлы уже отправлял». При папке в
// несколько десятков бланков выбрать дубль — обычное дело, а не ошибка.
//
// ПОЧЕМУ НЕ НА СЕРВЕРЕ. Записывать отклонённых в пакет предлагалось и было
// отклонено. Причина не в объёме — записи копеечные, — а в том, ЧТО пришлось бы
// хранить: имя файла. В HEAL-44 имена специально стирались при удалении анализа,
// потому что в них часто ФИО («Иванов_ИИ_анализ.pdf»). Новое место, где имена
// лежат бессрочно, отменяло бы ту работу.
//
// Клиент видел все исходы — он их и держит. Ноль роста, ноль персональных данных
// на сервере, переживает уход на Главную и перезапуск. Теряется при смене
// устройства или очистке данных: тогда виден только принятый состав, то есть
// ровно поведение до этой версии — не хуже.

export type RejectedReason =
  | 'duplicate'
  | 'not_sent'
  | 'send_failed'
  | 'too_large'
  | 'unsupported_type'
  | 'over_limit';

export type RejectedFile = {
  position: number;
  name: string;
  reason: RejectedReason;
  /** Для дубля — номер уже существующего анализа, чтобы было куда вести. */
  existingUploadId?: number | null;
};

const KEY_PREFIX = 'healthlab:batch-rejected:';

function keyFor(batchId: number): string {
  return `${KEY_PREFIX}${batchId}`;
}

export function saveRejected(batchId: number, files: RejectedFile[]): void {
  try {
    if (files.length === 0) {
      window.localStorage.removeItem(keyFor(batchId));

      return;
    }
    window.localStorage.setItem(keyFor(batchId), JSON.stringify(files));
  } catch {
    // Приватный режим или переполненное хранилище: человек увидит только
    // принятые файлы — то же, что было до этой версии.
  }
}

export function loadRejected(batchId: number): RejectedFile[] {
  try {
    const raw = window.localStorage.getItem(keyFor(batchId));
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    // Читаем чужие данные из хранилища — доверять форме нельзя: их мог
    // испортить старый формат или чужая вкладка.
    return parsed.filter(isRejectedFile);
  } catch {
    return [];
  }
}

/** Пакет разобран — держать исходы больше незачем. */
export function clearRejected(batchId: number): void {
  try {
    window.localStorage.removeItem(keyFor(batchId));
  } catch {
    // no-op
  }
}

function isRejectedFile(value: unknown): value is RejectedFile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;

  return (
    typeof v.position === 'number' &&
    typeof v.name === 'string' &&
    (v.reason === 'duplicate' ||
      v.reason === 'not_sent' ||
      v.reason === 'send_failed' ||
      v.reason === 'too_large' ||
      v.reason === 'unsupported_type' ||
      v.reason === 'over_limit')
  );
}

/**
 * Причины отсева одной фразой: «1 больше 10 МБ, 2 не поместились».
 *
 * Нужна там, где показать список исходов негде — когда после отсева остался
 * один файл и он уходит обычной одиночной загрузкой, без экрана пакета.
 * Молчание в этом месте и есть тот дефект, ради которого писался D-25.
 */
export function rejectionSummary(items: Array<{ reason: RejectedReason }>): string {
  const counts = new Map<RejectedReason, number>();

  for (const item of items) {
    counts.set(item.reason, (counts.get(item.reason) ?? 0) + 1);
  }

  return Array.from(counts.entries())
    .map(([reason, count]) => `${count} ${rejectedLabel(reason).toLowerCase()}`)
    .join(', ');
}

/** Подпись исхода. Формулировки разные не для красоты — они требуют разного. */
export function rejectedLabel(reason: RejectedReason): string {
  switch (reason) {
    case 'duplicate':
      // Искать файл не надо: он никуда не девался, он в истории.
      return 'Уже есть в истории';
    case 'not_sent':
      // А вот этот надо выбрать заново — до сервера он не дошёл.
      return 'Не отправлен';
    case 'send_failed':
      // Дошёл, но сервер его не принял. Причина техническая и человеку ничего не
      // говорит; важно, что файл НЕ загружен и его надо отправить ещё раз.
      return 'Не удалось загрузить';
    case 'too_large':
      return 'Больше 10 МБ';
    case 'unsupported_type':
      return 'Формат не поддерживается';
    case 'over_limit':
      // Файл в порядке, просто не поместился в этот заход. Формулировка не
      // винит человека и подсказывает, что делать: выбрать ещё раз, следующим
      // пакетом.
      return 'Не поместился — выберите следующим';
  }
}
